// store/modules/chatbot/actions.js
import axios from "axios";

const globalRequestSeq = new Map();
const globalInflight = new Map();
const reservationRequestSeq = new Map();
const reservationInflight = new Map();

export default {
  // 🌍 Lista global (pantalla general de chats)
  async retrieveGlobalChats(
    { commit },
    { user_id, property_id = "all", limit = 50 } = {}
  ) {
    console.log("📡 retrieveGlobalChats:", { user_id, property_id, limit });

    commit("SET_LOADING", true);

    const requestKey = `${user_id || "nouser"}:${property_id || "all"}:${limit}`;
    if (globalInflight.has(requestKey)) {
      return globalInflight.get(requestKey);
    }

    const nextSeq = (globalRequestSeq.get(requestKey) || 0) + 1;
    globalRequestSeq.set(requestKey, nextSeq);

    // Bandeja del Agente IA = WhatsApp + Web (conserje) + OTA (Booking/Airbnb…). El
    // endpoint filtra por UN canal, así que pedimos los tres y los fusionamos (cada canal
    // es una conversación distinta, con su propio icono y su propia pestaña en la reserva).
    const fetchChannel = (channel) =>
      axios
        .get("/retrieve-list-chat-bot", {
          params: { user_id, property_id, limit, channel },
        })
        // Etiqueta cada item con su canal (sabemos de qué llamada viene) para el icono.
        .then(({ data }) =>
          (data?.data || data || []).map((c) => ({ ...c, channel: c.channel || channel }))
        )
        .catch(() => []);

    const requestPromise = Promise.all([
      fetchChannel("whatsapp"),
      fetchChannel("web"),
      fetchChannel("ota"),
    ])
      .then((lists) => {
        const list = [].concat(...lists).sort((a, b) => {
          const an = Number(a?.has_new_message || 0);
          const bn = Number(b?.has_new_message || 0);
          if (an !== bn) return bn - an; // no leídos primero
          return new Date(b?.updated_at || 0) - new Date(a?.updated_at || 0);
        });

        if (globalRequestSeq.get(requestKey) === nextSeq) {
          commit("SET_LIST", list);
        } else {
          console.log("⏭️ retrieveGlobalChats ignorado por respuesta antigua", {
            requestKey,
            nextSeq,
            currentSeq: globalRequestSeq.get(requestKey),
          });
        }

        return list;
      })
      .catch((error) => {
        console.error("❌ Error retrieveGlobalChats:", error);
        throw error;
      })
      .finally(() => {
        if (globalInflight.get(requestKey) === requestPromise) {
          globalInflight.delete(requestKey);
        }
        commit("SET_LOADING", false);
      });

    globalInflight.set(requestKey, requestPromise);
    return requestPromise;
  },

  // 🏨 Lista filtrada por RESERVA (pantalla de detalle de reserva)
  async retrieveReservationChats(
    { commit },
    { user_id, property_id = "all", reservation_id, limit = 50 } = {}
  ) {
    console.log("📡 retrieveReservationChats:", {
      user_id,
      property_id,
      reservation_id,
      limit,
    });

    if (!reservation_id) {
      console.warn(
        "⚠️ reservation_id es requerido para retrieveReservationChats"
      );
      return [];
    }

    const requestKey = `${reservation_id}:${property_id || "all"}:${limit}`;
    if (reservationInflight.has(requestKey)) {
      return reservationInflight.get(requestKey);
    }

    const nextSeq = (reservationRequestSeq.get(requestKey) || 0) + 1;
    reservationRequestSeq.set(requestKey, nextSeq);

    const baseParams = { user_id, property_id, reservation_id, limit };
    const requestPromise = Promise.all([
      // WhatsApp (canal por defecto)
      axios.get("/retrieve-list-chat-bot", { params: { ...baseParams } }),
      // Canal web (Conserje / Portal de estancia). Tolerante a fallos.
      axios
        .get("/retrieve-list-chat-bot", { params: { ...baseParams, channel: "web" } })
        .catch(() => ({ data: [] })),
    ])
      .then(([waRes, webRes]) => {
        const waChats = waRes?.data?.data || waRes?.data || [];
        const webChats = webRes?.data?.data || webRes?.data || [];
        const chats = [
          ...(Array.isArray(waChats) ? waChats : []),
          ...(Array.isArray(webChats) ? webChats : []),
        ];

        if (reservationRequestSeq.get(requestKey) === nextSeq) {
          commit("SET_RESERVATION_CHATS", {
            reservationId: reservation_id,
            chats,
          });
        }

        return chats;
      })
      .catch((error) => {
        console.error("❌ Error retrieveReservationChats:", error);
        throw error;
      })
      .finally(() => {
        if (reservationInflight.get(requestKey) === requestPromise) {
          reservationInflight.delete(requestKey);
        }
      });

    reservationInflight.set(requestKey, requestPromise);
    return requestPromise;
  },

  // 🔁 Acción genérica (compatibilidad hacia atrás)
  async retrieveBotChats(
    { dispatch },
    { user_id, property_id = "all", reservation_id = null, limit = 50 } = {}
  ) {
    console.warn(
      "⚠️ retrieveBotChats es genérico. Usa retrieveGlobalChats o retrieveReservationChats"
    );

    if (reservation_id) {
      return dispatch("retrieveReservationChats", {
        user_id,
        property_id,
        reservation_id,
        limit,
      });
    } else {
      return dispatch("retrieveGlobalChats", {
        user_id,
        property_id,
        limit,
      });
    }
  },

  async changeStateQuestion({ commit }, question) {
    commit("SET_QUESTION", question);
  },

  async answerResponse({ commit }, payload) {
    commit("SET_ANSWER", payload);
  },

  setUnsavedChanges({ commit }, status) {
    commit("SET_UNSAVED_CHANGES", status);
  },
};
